/**
 * Procedural art.
 *
 * Everything the game draws is generated here at load time into canvas
 * textures. No image files, no licensing questions, and no asset pipeline to
 * maintain while the game design is still moving. The atlases are laid out the
 * way a real sprite sheet would be, so swapping in hand-drawn art later means
 * replacing this module rather than rewriting the renderer.
 */

import * as THREE from 'three/webgpu';
import {
  KIND_BARRACKS,
  KIND_HARVESTER,
  KIND_HQ,
  KIND_INFANTRY,
  KIND_REFINERY,
  KIND_TANK,
  KIND_WAR_FACTORY,
  KIND_COUNT,
  TILE_COUNT,
  TILE_DIRT,
  TILE_GRASS,
  TILE_ORE,
  TILE_ROCK,
  TILE_SAND,
  TILE_WATER,
} from '../game/constants.js';

/** Pixels per tile in the terrain atlas. */
const TILE_PX = 64;
/** Pixels per unit sprite cell. */
export const SPRITE_PX = 96;
/** Sprite facings baked into the atlas, matching classic 2D RTS rendering. */
export const SPRITE_FACINGS = 32;

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  return [canvas, ctx];
}

/** Deterministic hash noise, so the same tile always looks the same. */
function hash(x: number, y: number, seed: number): number {
  let h = Math.imul(x, 0x9e3779b9) ^ Math.imul(y, 0x85ebca6b) ^ seed;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491);
  h ^= h >>> 13;
  return (h >>> 0) / 0xffffffff;
}

interface TilePalette {
  base: string;
  speckle: string;
  speckle2: string;
  density: number;
}

const TILE_PALETTES: Record<number, TilePalette> = {
  [TILE_GRASS]: { base: '#3f5a2e', speckle: '#4a6b36', speckle2: '#354d27', density: 0.5 },
  [TILE_DIRT]: { base: '#5c4a33', speckle: '#6b5740', speckle2: '#4d3d2a', density: 0.45 },
  [TILE_SAND]: { base: '#9c8a5e', speckle: '#ab996b', speckle2: '#8a7a52', density: 0.35 },
  [TILE_WATER]: { base: '#1d3a5c', speckle: '#26496f', speckle2: '#17304c', density: 0.3 },
  [TILE_ROCK]: { base: '#4a4a4f', speckle: '#5c5c62', speckle2: '#38383c', density: 0.6 },
  [TILE_ORE]: { base: '#6b5a2a', speckle: '#c9a227', speckle2: '#8a7433', density: 0.55 },
};

/**
 * Builds the terrain atlas: one column of `TILE_COUNT` tiles, each drawn as a
 * noisy base with speckles so large fields of one terrain do not look flat.
 */
export function buildTerrainAtlas(): THREE.Texture {
  const [canvas, ctx] = makeCanvas(TILE_PX, TILE_PX * TILE_COUNT);

  for (let tile = 0; tile < TILE_COUNT; tile++) {
    const pal = TILE_PALETTES[tile] ?? TILE_PALETTES[TILE_GRASS];
    const oy = tile * TILE_PX;

    ctx.fillStyle = pal.base;
    ctx.fillRect(0, oy, TILE_PX, TILE_PX);

    const speckles = Math.floor(TILE_PX * TILE_PX * pal.density * 0.08);
    for (let i = 0; i < speckles; i++) {
      const rx = hash(i, tile, 1) * TILE_PX;
      const ry = hash(i, tile, 2) * TILE_PX;
      const size = 1 + hash(i, tile, 3) * 2.5;
      ctx.fillStyle = hash(i, tile, 4) > 0.5 ? pal.speckle : pal.speckle2;
      ctx.fillRect(rx, oy + ry, size, size);
    }

    if (tile === TILE_WATER) {
      // Horizontal ripples read as water even without animation.
      ctx.strokeStyle = 'rgba(120,180,220,0.18)';
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 6; i++) {
        const y = oy + (i + hash(i, tile, 5)) * (TILE_PX / 6);
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x <= TILE_PX; x += 8) {
          ctx.lineTo(x, y + Math.sin(x * 0.2 + i) * 2);
        }
        ctx.stroke();
      }
    }

    if (tile === TILE_ORE) {
      // Gold nuggets, the visual cue that a tile is worth harvesting.
      for (let i = 0; i < 14; i++) {
        const rx = hash(i, tile, 6) * TILE_PX;
        const ry = hash(i, tile, 7) * TILE_PX;
        const r = 2 + hash(i, tile, 8) * 3;
        const grad = ctx.createRadialGradient(rx, oy + ry, 0, rx, oy + ry, r);
        grad.addColorStop(0, '#ffd94a');
        grad.addColorStop(1, '#a8801d');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(rx, oy + ry, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    if (tile === TILE_ROCK) {
      // Angular facets so cliffs read as impassable.
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = hash(i, tile, 9) > 0.5 ? '#5f5f66' : '#3a3a3e';
        ctx.beginPath();
        const cx = hash(i, tile, 10) * TILE_PX;
        const cy = oy + hash(i, tile, 11) * TILE_PX;
        const r = 6 + hash(i, tile, 12) * 12;
        ctx.moveTo(cx + r, cy);
        for (let k = 1; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2;
          const rr = r * (0.6 + hash(i * 6 + k, tile, 13) * 0.5);
          ctx.lineTo(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
        }
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/** Fraction of the atlas height occupied by one tile. */
export const TILE_ATLAS_STEP = 1 / TILE_COUNT;

/**
 * Draws one unit or structure, pointing right (angle 0), centred in a
 * `SPRITE_PX` cell. Team colour is applied by the shader, so anything that
 * should tint is drawn in the magenta key colour.
 */
type Painter = (ctx: CanvasRenderingContext2D, s: number) => void;

const TEAM_KEY = '#ff00ff';

const PAINTERS: Record<number, Painter> = {
  [KIND_INFANTRY]: (ctx, s) => {
    const c = s / 2;
    // Body.
    ctx.fillStyle = TEAM_KEY;
    ctx.beginPath();
    ctx.ellipse(c, c, s * 0.13, s * 0.17, 0, 0, Math.PI * 2);
    ctx.fill();
    // Helmet.
    ctx.fillStyle = '#2b2b2b';
    ctx.beginPath();
    ctx.arc(c + s * 0.02, c, s * 0.1, 0, Math.PI * 2);
    ctx.fill();
    // Rifle, pointing along +X.
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = s * 0.035;
    ctx.beginPath();
    ctx.moveTo(c, c - s * 0.05);
    ctx.lineTo(c + s * 0.28, c - s * 0.05);
    ctx.stroke();
  },

  [KIND_TANK]: (ctx, s) => {
    const c = s / 2;
    // Treads.
    ctx.fillStyle = '#26262a';
    ctx.fillRect(c - s * 0.26, c - s * 0.28, s * 0.52, s * 0.11);
    ctx.fillRect(c - s * 0.26, c + s * 0.17, s * 0.52, s * 0.11);
    // Hull.
    ctx.fillStyle = TEAM_KEY;
    roundRect(ctx, c - s * 0.24, c - s * 0.2, s * 0.48, s * 0.4, s * 0.05);
    ctx.fill();
    // Hull shading, so the sprite has some depth.
    ctx.fillStyle = 'rgba(0,0,0,0.22)';
    ctx.fillRect(c - s * 0.24, c + s * 0.06, s * 0.48, s * 0.14);
    ctx.strokeStyle = '#15151a';
    ctx.lineWidth = s * 0.02;
    roundRect(ctx, c - s * 0.24, c - s * 0.2, s * 0.48, s * 0.4, s * 0.05);
    ctx.stroke();
  },

  [KIND_HARVESTER]: (ctx, s) => {
    const c = s / 2;
    ctx.fillStyle = '#26262a';
    ctx.fillRect(c - s * 0.3, c - s * 0.3, s * 0.6, s * 0.12);
    ctx.fillRect(c - s * 0.3, c + s * 0.18, s * 0.6, s * 0.12);
    ctx.fillStyle = TEAM_KEY;
    roundRect(ctx, c - s * 0.28, c - s * 0.22, s * 0.5, s * 0.44, s * 0.05);
    ctx.fill();
    // Collection scoop at the front.
    ctx.fillStyle = '#8a8a92';
    roundRect(ctx, c + s * 0.2, c - s * 0.16, s * 0.12, s * 0.32, s * 0.03);
    ctx.fill();
    ctx.strokeStyle = '#15151a';
    ctx.lineWidth = s * 0.02;
    roundRect(ctx, c - s * 0.28, c - s * 0.22, s * 0.5, s * 0.44, s * 0.05);
    ctx.stroke();
  },

  [KIND_HQ]: (ctx, s) => buildingPainter(ctx, s, 0.42, '#6a6a72', true),
  [KIND_REFINERY]: (ctx, s) => {
    buildingPainter(ctx, s, 0.42, '#5c5a4a', false);
    const c = s / 2;
    // Silo, to distinguish it at a glance.
    ctx.fillStyle = '#8a7a3a';
    ctx.beginPath();
    ctx.arc(c + s * 0.2, c - s * 0.16, s * 0.13, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#15151a';
    ctx.lineWidth = s * 0.02;
    ctx.stroke();
  },
  [KIND_BARRACKS]: (ctx, s) => {
    buildingPainter(ctx, s, 0.34, '#4a5a3a', false);
    const c = s / 2;
    // Door.
    ctx.fillStyle = '#20201f';
    ctx.fillRect(c + s * 0.16, c - s * 0.08, s * 0.16, s * 0.16);
  },
  [KIND_WAR_FACTORY]: (ctx, s) => {
    buildingPainter(ctx, s, 0.42, '#4a4a55', false);
    const c = s / 2;
    // Roll-up door with hazard stripes.
    ctx.fillStyle = '#20201f';
    ctx.fillRect(c + s * 0.1, c - s * 0.18, s * 0.3, s * 0.36);
    ctx.fillStyle = '#c9a227';
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(c + s * 0.1, c - s * 0.18 + i * s * 0.09, s * 0.3, s * 0.03);
    }
  },
};

function buildingPainter(
  ctx: CanvasRenderingContext2D,
  s: number,
  half: number,
  roof: string,
  tower: boolean,
): void {
  const c = s / 2;
  // Drop shadow grounds the structure on the terrain.
  ctx.fillStyle = 'rgba(0,0,0,0.3)';
  roundRect(ctx, c - half * s + s * 0.03, c - half * s + s * 0.05, half * 2 * s, half * 2 * s, s * 0.04);
  ctx.fill();

  ctx.fillStyle = TEAM_KEY;
  roundRect(ctx, c - half * s, c - half * s, half * 2 * s, half * 2 * s, s * 0.04);
  ctx.fill();

  // Roof plate inset, drawn in a neutral colour so the team tint stays a border.
  ctx.fillStyle = roof;
  roundRect(
    ctx,
    c - half * s * 0.78,
    c - half * s * 0.78,
    half * 1.56 * s,
    half * 1.56 * s,
    s * 0.03,
  );
  ctx.fill();

  // Panel lines.
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = s * 0.012;
  for (let i = 1; i < 4; i++) {
    const y = c - half * s * 0.78 + (i / 4) * half * 1.56 * s;
    ctx.beginPath();
    ctx.moveTo(c - half * s * 0.78, y);
    ctx.lineTo(c + half * s * 0.78, y);
    ctx.stroke();
  }

  if (tower) {
    ctx.fillStyle = '#8a8a92';
    ctx.beginPath();
    ctx.arc(c, c, s * 0.11, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#c9c9d2';
    ctx.beginPath();
    ctx.arc(c, c, s * 0.055, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.strokeStyle = '#15151a';
  ctx.lineWidth = s * 0.022;
  roundRect(ctx, c - half * s, c - half * s, half * 2 * s, half * 2 * s, s * 0.04);
  ctx.stroke();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Body sprite atlas: one row per entity kind, one column per baked facing.
 * Buildings occupy only the first column since they never rotate.
 */
export function buildUnitAtlas(): THREE.Texture {
  const [canvas, ctx] = makeCanvas(SPRITE_PX * SPRITE_FACINGS, SPRITE_PX * KIND_COUNT);

  for (let kind = 0; kind < KIND_COUNT; kind++) {
    const painter = PAINTERS[kind];
    if (!painter) continue;
    const facings = kind >= KIND_HQ ? 1 : SPRITE_FACINGS;
    for (let f = 0; f < facings; f++) {
      ctx.save();
      ctx.translate(f * SPRITE_PX, kind * SPRITE_PX);
      ctx.beginPath();
      ctx.rect(0, 0, SPRITE_PX, SPRITE_PX);
      ctx.clip();
      // Rotate about the cell centre so each column is one baked facing.
      ctx.translate(SPRITE_PX / 2, SPRITE_PX / 2);
      ctx.rotate((f / SPRITE_FACINGS) * Math.PI * 2);
      ctx.translate(-SPRITE_PX / 2, -SPRITE_PX / 2);
      painter(ctx, SPRITE_PX);
      ctx.restore();
    }
    // Buildings reuse column 0 for every facing lookup.
    if (facings === 1) {
      for (let f = 1; f < SPRITE_FACINGS; f++) {
        ctx.drawImage(
          canvas,
          0,
          kind * SPRITE_PX,
          SPRITE_PX,
          SPRITE_PX,
          f * SPRITE_PX,
          kind * SPRITE_PX,
          SPRITE_PX,
          SPRITE_PX,
        );
      }
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Turret atlas: one row per kind, one column per facing. Only turreted units
 * draw anything; the rest are transparent so the same shader can run for all.
 */
export function buildTurretAtlas(): THREE.Texture {
  const [canvas, ctx] = makeCanvas(SPRITE_PX * SPRITE_FACINGS, SPRITE_PX * KIND_COUNT);

  for (let f = 0; f < SPRITE_FACINGS; f++) {
    ctx.save();
    ctx.translate(f * SPRITE_PX, KIND_TANK * SPRITE_PX);
    ctx.beginPath();
    ctx.rect(0, 0, SPRITE_PX, SPRITE_PX);
    ctx.clip();
    ctx.translate(SPRITE_PX / 2, SPRITE_PX / 2);
    ctx.rotate((f / SPRITE_FACINGS) * Math.PI * 2);
    ctx.translate(-SPRITE_PX / 2, -SPRITE_PX / 2);

    const s = SPRITE_PX;
    const c = s / 2;
    // Barrel.
    ctx.fillStyle = '#33333a';
    ctx.fillRect(c, c - s * 0.03, s * 0.36, s * 0.06);
    ctx.strokeStyle = '#15151a';
    ctx.lineWidth = s * 0.015;
    ctx.strokeRect(c, c - s * 0.03, s * 0.36, s * 0.06);
    // Turret dome, team-tinted.
    ctx.fillStyle = TEAM_KEY;
    ctx.beginPath();
    ctx.arc(c, c, s * 0.14, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#15151a';
    ctx.lineWidth = s * 0.02;
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.beginPath();
    ctx.arc(c - s * 0.03, c - s * 0.04, s * 0.06, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/** Soft radial blob, reused for muzzle flashes, explosions and selection glow. */
export function buildParticleTexture(): THREE.Texture {
  const size = 64;
  const [canvas, ctx] = makeCanvas(size, size);
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.65)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Team colours, indexed by player slot. */
export const TEAM_COLORS: THREE.Color[] = [
  new THREE.Color('#c8392b'),
  new THREE.Color('#2b6fc8'),
  new THREE.Color('#3fa14a'),
  new THREE.Color('#c9a227'),
];

export function teamColor(player: number): THREE.Color {
  return TEAM_COLORS[player % TEAM_COLORS.length];
}
